export interface TextExpansionResult {
  success: boolean;
  originalText?: string;
  expandedText?: string;
  error?: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface AIServiceStatus {
  configured: boolean;
  connected: boolean;
  status: 'ready' | 'not-configured' | 'connection-failed';
}

// Get server URL based on environment
function getServerUrl(): string {
  // In development with Vite proxy, use relative URLs
  if (import.meta.env.DEV) {
    return '';
  }
  
  // In production, use the current origin
  return window.location.origin;
}

export class AITextService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getServerUrl();
  }

  /**
   * Expand a description using OpenAI GPT-4o-mini
   */
  async expandDescription(
    text: string,
    context?: {
      gewerk?: string;
      projectType?: string;
      additionalContext?: string;
    }
  ): Promise<TextExpansionResult> {
    try {
      if (!text || text.trim().length === 0) {
        return {
          success: false,
          error: 'Keine Beschreibung zum Erweitern vorhanden',
          originalText: text
        };
      }

      console.log(`🤖 Expanding description: "${text.substring(0, 50)}..."`);

      const response = await fetch(`${this.baseUrl}/api/ai/expand-description`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text.trim(),
          gewerk: context?.gewerk,
          projectType: context?.projectType,
          additionalContext: context?.additionalContext
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (!result.success) {
        return {
          success: false,
          error: result.error || 'Unbekannter Fehler bei der Texterweiterung',
          originalText: result.originalText || text
        };
      }

      console.log(`✅ Description expanded successfully: ${text.length} -> ${result.data.expandedText.length} characters`);

      return {
        success: true,
        originalText: result.data.originalText,
        expandedText: result.data.expandedText,
        usage: result.data.usage
      };

    } catch (error) {
      console.error('AI text expansion error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler bei der Texterweiterung',
        originalText: text
      };
    }
  }

  /**
   * Rewrite a text with a free-form manual instruction
   * (e.g. "VOB-konform anpassen", "Rechtschreibung korrigieren").
   * Uses the same server-side safeguards as description optimization,
   * so GAEB-Pflichttexte bleiben erhalten.
   */
  async rewriteText(
    text: string,
    instruction: string,
    gewerk?: string,
    signal?: AbortSignal,
  ): Promise<{ success: boolean; rewritten?: string; error?: string; safeguarded?: boolean; note?: string }> {
    try {
      if (!text || text.trim().length === 0) {
        return {
          success: false,
          error: 'Kein Text zum Umschreiben vorhanden'
        };
      }

      if (!instruction || instruction.trim().length === 0) {
        return {
          success: false,
          error: 'Bitte geben Sie eine Anweisung an'
        };
      }

      const response = await fetch(`${this.baseUrl}/api/ai/rewrite`, {
        method: 'POST',
        signal,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text.trim(),
          instruction: instruction.trim(),
          gewerk
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return {
          success: false,
          error: result.error || `HTTP ${response.status}: ${response.statusText}`
        };
      }

      return {
        success: true,
        rewritten: result.rewritten,
        safeguarded: result.safeguarded,
        note: result.note
      };

    } catch (error) {
      console.error('AI text rewrite error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler bei der KI-Textüberarbeitung'
      };
    }
  }

  /**
   * Check AI service status
   */
  async getStatus(): Promise<AIServiceStatus> {
    try {
      const response = await fetch(`${this.baseUrl}/api/ai/status`);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Status check failed');
      }

      return result.data;

    } catch (error) {
      console.error('AI status check error:', error);
      return {
        configured: false,
        connected: false,
        status: 'connection-failed'
      };
    }
  }

  /**
   * Check if AI service is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      const status = await this.getStatus();
      return status.configured && status.connected;
    } catch (error) {
      console.error('AI availability check error:', error);
      return false;
    }
  }
}

// Export singleton instance
export const aiTextService = new AITextService();
